/**
 * Diagnostics Redux Slice
 * ═══════════════════════════════════════════════════════════════════════════
 * SEPARATION OF CONCERNS:
 * - authSlice owns JWT / authenticated user / org-pfuser associations
 *   (identity + tenant context — this is Redux's job, always correct once
 *   loaded, regardless of what any individual TSX component does with it).
 * - diagnosticsSlice owns "did something just fail" — a TSX component
 *   failing to resolve organization context, a 4xx/5xx API response, etc.
 *   These are MUTUALLY EXCLUSIVE concerns: a component-level binding error
 *   is not evidence that auth/org state itself is wrong.
 *
 * PlatformHeader's bug-report icon reads `activeError` from this slice to
 * control its own visibility/active state, independent of auth/org state.
 * Any part of the app (apiService.ts request failures, component-level
 * catches, etc.) can dispatch setActiveError() without touching authSlice.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface DetectedError {
    errorType: string; // '500 Error' | '400 Error' | 'Network Error' | etc.
    statusCode: number | null; // Real HTTP status (422, 404, 401, 403, 500, etc.) - null for Network Error
    message: string;
    route: string;
    moduleSeq: number | null;
    tabSeq: number | null;
    // Set when the failure happened while ComponentViewer had a component open in its
    // preview dialog - moduleSeq/tabSeq above describe the HOST route (e.g. Registry
    // Manager), not the actual component being previewed/tested. See componentPreviewContext.ts.
    previewSeq: number | null;
    previewFriendlyName: string | null;
    timestamp: string;
}

interface DiagnosticsState {
    activeError: DetectedError | null;
}

const initialState: DiagnosticsState = {
    activeError: null,
};

const diagnosticsSlice = createSlice({
    name: 'diagnostics',
    initialState,
    reducers: {
        setActiveError(state, action: PayloadAction<DetectedError>) {
            state.activeError = action.payload;
        },
        clearActiveError(state) {
            state.activeError = null;
        },
    },
});

export const { setActiveError, clearActiveError } = diagnosticsSlice.actions;
export default diagnosticsSlice.reducer;
